// Ledger API service for making API calls to the backend

import { apiBaseUrl } from '../../config/env';

const API_URL = apiBaseUrl;

export interface LedgerAccount {
  id: string;
  userId: string;
  personName: string;
  personId?: string;
  balance: string;
  totalReceived: string;
  totalPaid: string;
  lastTransactionDate?: string;
  isBookmarked: boolean;
  type: string;
  note?: string;
  isShared: boolean;
  linkedUserId?: string; // DEPRECATED
  linkStatus: string; // DEPRECATED
  createdBy: string;
  isViewOnly: boolean;
  createdAt: string;
  updatedAt: string;
  participants?: LedgerParticipant[]; // NEW: Multiple participants
}

export interface LedgerParticipant {
  id: string;
  userId: string;
  name: string;
  role: 'owner' | 'viewer';
  status: 'pending' | 'accepted' | 'rejected';
  joinedAt: string;
}

export interface LedgerTransaction {
  id: string;
  ledgerId: string;
  userId: string;
  createdByUserId: string; // Who created this transaction
  createdByName?: string; // Name of the creator
  type: 'credit' | 'debit';
  amount: string;
  title: string; // Mandatory title
  note?: string; // Optional note
  tags?: string[];
  date: string;
  createdAt: string;
  updatedAt: string;
  personName?: string; // Populated when fetching all transactions
  participantUserIds?: string[]; // NEW: Array of user IDs involved
  participantName?: string; // NEW: For people not in the app
}

export interface PaginatedResponse<T> {
  data?: T[];
  accounts?: LedgerAccount[];
  transactions?: LedgerTransaction[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

// Get all ledger accounts
export const getLedgers = async (
  token: string,
  filter: 'all' | 'bookmarked' | 'shared' | 'pending' = 'all',
  page: number = 1,
  limit: number = 10
): Promise<PaginatedResponse<LedgerAccount>> => {
  const response = await fetch(
    `${API_URL}/api/ledgers?filter=${filter}&page=${page}&limit=${limit}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch ledgers');
  }

  return response.json();
};

// Get a specific ledger account
export const getLedger = async (
  token: string,
  ledgerId: string
): Promise<LedgerAccount> => {
  const response = await fetch(
    `${API_URL}/api/ledgers/${ledgerId}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch ledger');
  }

  const data = await response.json();
  return data.ledger;
};

// Legacy method (kept for compatibility)
export const getLedgerById = async (
  token: string,
  ledgerId: string
): Promise<{ ledger: LedgerAccount; recentTransactions: LedgerTransaction[] }> => {
  const response = await fetch(
    `${API_URL}/api/ledgers/${ledgerId}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch ledger');
  }

  return response.json();
};

// Create a new ledger account
export const createLedger = async (
  token: string,
  data: {
    personName: string;
    personId?: string;
    isBookmarked?: boolean;
    type?: string;
    note?: string;
    sharedUserIds?: string[];
  }
): Promise<{ ledger: LedgerAccount }> => {
  const response = await fetch(
    `${API_URL}/api/ledgers`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create ledger');
  }

  return response.json();
};

// Update a ledger account
export const updateLedger = async (
  token: string,
  ledgerId: string,
  data: {
    personName?: string;
    isBookmarked?: boolean;
    type?: string;
    note?: string;
  }
): Promise<{ ledger: LedgerAccount }> => {
  const response = await fetch(
    `${API_URL}/api/ledgers/${ledgerId}`,
    {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update ledger');
  }

  return response.json();
};

// Delete a ledger account
export const deleteLedger = async (
  token: string,
  ledgerId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await fetch(
    `${API_URL}/api/ledgers/${ledgerId}`,
    {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete ledger');
  }

  return response.json();
};

// Get all transactions across all ledgers
export const getAllTransactions = async (
  token: string,
  type: 'credit' | 'debit' | 'all' = 'all',
  page: number = 1,
  limit: number = 10
): Promise<PaginatedResponse<LedgerTransaction>> => {
  const response = await fetch(
    `${API_URL}/api/ledgers/transactions/all?type=${type}&page=${page}&limit=${limit}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch transactions');
  }

  return response.json();
};

// Get transactions for a specific ledger
export const getTransactionsByLedger = async (
  token: string,
  ledgerId: string,
  type: 'credit' | 'debit' | 'all' = 'all',
  page: number = 1,
  limit: number = 10
): Promise<PaginatedResponse<LedgerTransaction>> => {
  const response = await fetch(
    `${API_URL}/api/ledgers/${ledgerId}/transactions?type=${type}&page=${page}&limit=${limit}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch transactions');
  }

  return response.json();
};

// Create a new transaction
export const createTransaction = async (
  token: string,
  ledgerId: string,
  data: {
    type: 'credit' | 'debit';
    amount: number;
    note?: string;
    date?: string;
    tags?: string[];
    participantUserIds?: string[]; // NEW
    participantName?: string; // NEW
  }
): Promise<{ transaction: LedgerTransaction; ledger: LedgerAccount }> => {
  const response = await fetch(
    `${API_URL}/api/ledgers/${ledgerId}/transactions`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create transaction');
  }

  return response.json();
};

// Get a specific transaction
export const getTransaction = async (
  token: string,
  ledgerId: string,
  transactionId: string
): Promise<LedgerTransaction> => {
  const response = await fetch(
    `${API_URL}/api/ledgers/${ledgerId}/transactions/${transactionId}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch transaction');
  }

  const data = await response.json();
  return data.transaction;
};

// Update a transaction
export const updateTransaction = async (
  token: string,
  ledgerId: string,
  transactionId: string,
  data: {
    type?: 'credit' | 'debit';
    amount?: number;
    note?: string;
    date?: string;
    tags?: string[];
    participantUserIds?: string[]; // NEW
    participantName?: string; // NEW
  }
): Promise<{ transaction: LedgerTransaction; ledger: LedgerAccount }> => {
  const response = await fetch(
    `${API_URL}/api/ledgers/${ledgerId}/transactions/${transactionId}`,
    {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update transaction');
  }

  return response.json();
};

// Delete a transaction
export const deleteTransaction = async (
  token: string,
  ledgerId: string,
  transactionId: string
): Promise<{ success: boolean; message: string; updatedLedger: LedgerAccount }> => {
  const response = await fetch(
    `${API_URL}/api/ledgers/${ledgerId}/transactions/${transactionId}`,
    {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete transaction');
  }

  return response.json();
};

// Invite API Types
export interface LedgerInvite {
  id: string;
  ledgerId: string;
  fromUserId: string;
  fromUserName: string;
  toUserId: string;
  status: 'pending' | 'accepted' | 'rejected';
  createdAt: string;
  ledger?: {
    personName: string;
    type: string;
    balance: string;
  };
}

// Get pending invites
export const getPendingInvites = async (token: string): Promise<LedgerInvite[]> => {
  const response = await fetch(`${API_URL}/api/ledgers/invites`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch invites');
  }

  const data = await response.json();
  return data.invites || [];
};

// Accept an invite
export const acceptInvite = async (token: string, inviteId: string): Promise<LedgerAccount> => {
  const response = await fetch(`${API_URL}/api/ledgers/invites/${inviteId}/accept`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to accept invite');
  }

  const data = await response.json();
  return data.ledger;
};

// Reject an invite
export const rejectInvite = async (token: string, inviteId: string): Promise<{ success: boolean }> => {
  const response = await fetch(`${API_URL}/api/ledgers/invites/${inviteId}/reject`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reject invite');
  }

  return response.json();
};

// Share a ledger with multiple users
export const shareLedger = async (
  token: string,
  ledgerId: string,
  toUserIds: string[]
): Promise<{ invites: LedgerInvite[] }> => {
  const response = await fetch(`${API_URL}/api/ledgers/${ledgerId}/share`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ toUserIds }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to share ledger');
  }

  return response.json();
};

// Leave a shared ledger
export const leaveLedger = async (token: string, ledgerId: string): Promise<{ success: boolean; message: string }> => {
  const response = await fetch(`${API_URL}/api/ledgers/${ledgerId}/leave`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to leave ledger');
  }

  return response.json();
};

// Remove a participant from a shared ledger (owner only)
export const removeParticipant = async (token: string, ledgerId: string, userId: string): Promise<{ success: boolean; message: string }> => {
  const response = await fetch(`${API_URL}/api/ledgers/${ledgerId}/participants/${userId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to remove participant');
  }

  return response.json();
};

// Export ledger to Excel - returns the file URL/path
export const exportLedgerToExcel = async (token: string, ledgerId: string): Promise<string> => {
  const response = await fetch(`${API_URL}/api/ledgers/${ledgerId}/export`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to export ledger');
  }

  // Return the blob
  const blob = await response.blob();
  return URL.createObjectURL(blob);
};
